'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  FolderKanban,
  Headphones,
  Phone,
  Megaphone,
  Code2,
  TicketCheck,
  UserCog,
  CreditCard,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  TrendingUp,
  LayoutGrid,
  Globe,
  MonitorCheck,
  Award,
  X,
  ClipboardList,
  Images,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { id: 'nav-arrows-home', label: 'Arrows Groups Home', icon: Globe, href: '/arrows-home', group: 'public' },
  { id: 'nav-gallery', label: 'Gallery', icon: Images, href: '/gallery', group: 'public' },
  { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/', group: 'main' },
  { id: 'nav-services', label: 'Services', icon: Briefcase, href: '/services', group: 'main' },
  { id: 'nav-clients', label: 'Clients', icon: Users, href: '/clients', group: 'main' },
  { id: 'nav-leads', label: 'Leads / CRM', icon: TrendingUp, href: '/leads', group: 'main', badge: 7 },
  { id: 'nav-projects', label: 'Projects', icon: FolderKanban, href: '/projects', group: 'operations' },
  { id: 'nav-portfolio', label: 'Project Portfolio', icon: Award, href: '/portfolio', group: 'operations' },
  { id: 'nav-bpo', label: 'BPO Center', icon: Headphones, href: '/bpo', group: 'operations', badge: 3 },
  { id: 'nav-voip', label: 'VOIP / Calls', icon: Phone, href: '/voip', group: 'operations' },
  { id: 'nav-marketing', label: 'Digital Marketing', icon: Megaphone, href: '/marketing', group: 'operations' },
  { id: 'nav-webdev', label: 'Web Development', icon: Code2, href: '/web-development', group: 'operations' },
  { id: 'nav-tickets', label: 'Support Tickets', icon: TicketCheck, href: '/support-tickets', group: 'support', badge: 12 },
  { id: 'nav-employee-portal', label: 'My Workspace', icon: MonitorCheck, href: '/employee-portal', group: 'employee' },
  { id: 'nav-employees', label: 'Employees', icon: UserCog, href: '/employees', group: 'admin' },
  { id: 'nav-invoices', label: 'Invoices & Payments', icon: CreditCard, href: '/invoices', group: 'admin' },
  { id: 'nav-reports', label: 'Reports & Analytics', icon: BarChart3, href: '/reports', group: 'admin' },
  { id: 'nav-onboarding', label: 'Admin Onboarding', icon: ClipboardList, href: '/admin-onboarding', group: 'admin' },
  { id: 'nav-settings', label: 'Settings', icon: Settings, href: '/settings', group: 'admin' },
  { id: 'nav-client-portal', label: 'Client Portal', icon: LayoutGrid, href: '/client-portal', group: 'client' },
  { id: 'nav-client-projects', label: 'My Projects', icon: FolderKanban, href: '/client-portal/projects', group: 'client' },
  { id: 'nav-client-invoices', label: 'My Invoices', icon: CreditCard, href: '/client-portal/invoices', group: 'client' },
  { id: 'nav-client-tickets', label: 'My Tickets', icon: TicketCheck, href: '/client-portal/support-tickets', group: 'client', badge: 2 },
  { id: 'nav-client-request', label: 'Request Service', icon: Briefcase, href: '/client-portal/request-service', group: 'client' },
];

const groups = [
  { key: 'public', label: 'Public Site' },
  { key: 'main', label: 'Overview' },
  { key: 'operations', label: 'Operations' },
  { key: 'support', label: 'Support' },
  { key: 'employee', label: 'Employee Portal' },
  { key: 'admin', label: 'Administration' },
  { key: 'client', label: 'Client Portal' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const pathname = usePathname();

  const sidebarContent = (isMobile: boolean) => (
    <aside
      className={`
        flex flex-col h-full bg-card border-r border-border
        transition-all duration-300 ease-in-out flex-shrink-0
        ${isMobile ? 'w-72' : (collapsed ? 'w-16' : 'w-60')}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-3 flex-shrink-0 ${(!isMobile && collapsed) ? 'justify-center' : 'justify-between'}`}>
        <div className="flex items-center gap-2 overflow-hidden">
          <AppLogo size={32} />
          {(isMobile || !collapsed) && (
            <span className="font-semibold text-base text-foreground tracking-tight whitespace-nowrap">
              Arrows
            </span>
          )}
        </div>
        {isMobile ? (
          <button
            onClick={onMobileClose}
            className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        ) : !collapsed ? (
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        ) : null}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-thin">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group.key);
          return (
            <div key={`group-${group.key}`} className="mb-4">
              {(isMobile || !collapsed) && (
                <p className="px-3 mb-1 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
                const showCollapsed = !isMobile && collapsed;
                return (
                  <Link key={item.id} href={item.href} onClick={isMobile ? onMobileClose : undefined}>
                    <div
                      className={`sidebar-nav-item mx-2 ${isActive ? 'active' : ''} ${showCollapsed ? 'justify-center px-2' : ''}`}
                      title={showCollapsed ? item.label : undefined}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      {(isMobile || !collapsed) && (
                        <>
                          <span className="flex-1 text-sm">{item.label}</span>
                          {item.badge && (
                            <span className="ml-auto bg-danger text-white text-2xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                      {showCollapsed && item.badge && (
                        <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-danger rounded-full" />
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3 flex-shrink-0">
        {(!isMobile && collapsed) ? (
          <button
            onClick={onToggle}
            className="w-full flex justify-center p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary">RA</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">Ryan Ashford</p>
              <p className="text-2xs text-muted-foreground truncate">Super Admin</p>
            </div>
            <button className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <Bell size={15} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex h-screen">
        {sidebarContent(false)}
      </div>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <div className="relative h-full">
            {sidebarContent(true)}
          </div>
        </div>
      )}
    </>
  );
}